import {CLIssues} from '../issues/issues.types';
import {ClinchMedia, ShortMediaType} from '../media/media.types';
import {DateTime} from 'luxon';
import {IntersectionUser} from '../userClients/userClients.types';
import ObjectStatus from '../enums/static/ObjectStatus';
import {SocialTrackingPixel, SocialNetworkPage} from '../social/social.types';
import {Dispatch} from 'react';
import {DoubleClickCampaignManagerParams} from '../decisionTree/decisionTree.types';

enum ExternalAdStatus {
  Planned = 10,
  Pending = 20,
  SyncingWithPlatform = 25,
  PendingSync = 28,
  Failed = 30,
  Published = 40,
  PlannedForUpdate = 50,
  PlannedForDeletion = 60,
  FailedGenerate = 80,
  RemovedFromPlatform = 999,
}

enum GenerateAdsIssue {
  MissingConnection = 0,
  ContainErrors = 1,
  AdGroupsExcceededLimit = 2,
  NoVariationGenerated = 3,
}

enum GenerateAdsStatus {
  NoChangesRequired = 0,
  Executing = 1,
  HasIssues = 2,
}

enum SocialCampaignFlowStatus {
  Setup = 0,
  Publish = 1,
  Publishing = 2,
  PublishFailed = 3,
  Activate = 4,
  PendingActivation = 5,
  Activating = 6,
  Active = 7,
  Inactive = 8,

  GenerateAds = 9,
  Generating = 10,
  Rendering = 11,
  GenerateAdsFailed = 12,

  ReadyToSync = 13,
  Syncing = 14,
  SyncFailed = 15,
}

enum AdReviewStatus {
  Unknown = 0,
  InProgress = 10,
  Passed = 20,
  Failed = 30,
}

enum AdState {
  Unknown = 0,
  AdSetOff = 1,
  CampaignOff = 2,
  AdSetEnded = 3,
  Failures = 4,
  Paused = 5,
  Active = 6,
}

enum CarouselProductSelectionType {
  SameItemsForAllCards = 10,
  DifferentItemsPerCard = 20,
}

enum FacebookPosition {
  Feed = 'feed',
  RightHandColumn = 'right_hand_column',
  InstantArticle = 'instant_article',
  Marketplace = 'marketplace',
  VideoFeeds = 'video_feeds',
  Story = 'story',
  Search = 'search',
  InstreamVideo = 'instream_video',
  InstreamBanner = 'instream_banner',
  GroupsFeed = 'groups_feed',
  BizDiscoFeed = 'biz_disco_feed',
  FacebookReels = 'facebook_reels',
  FacebookReelsOverlay = 'facebook_reels_overlay',
}

enum InstagramPosition {
  Stream = 'stream',
  Story = 'story',
  Shop = 'shop',
  Explore = 'explore',
  Reels = 'reels',
}

enum MessengerPosition {
  MessengerHome = 'messenger_home',
  SponsoredMessages = 'sponsored_messages',
  Story = 'story',
}

enum AudienceNetworkPosition {
  Classic = 'classic',
  RewardedVideo = 'rewarded_video',
  InstreamVideo = 'instream_video',
}

enum SocialAdGroupParamKey {
  Age = 'Age',
  Location = 'Location - Living in',
  ExcludedLocation = 'Excluded Location',
  Gender = 'Gender',
  AdGroupType = 'Adgroup Type',
}

interface AdGroupParam {
  key: SocialAdGroupParamKey;
  values: string[];
  order: number;
}

enum DV360LineItemType {
  EfficientReach = 'LINE_ITEM_TYPE_YOUTUBE_AND_PARTNERS_REACH',
  VideoViews = 'LINE_ITEM_TYPE_YOUTUBE_AND_PARTNERS_VIEW',
}

type DV360LineItem = {
  id: string;
  name: string;
  status: ObjectStatus;
  state: string;
  isResponsive: boolean;
  isLineItemConnectedTV: boolean;
  type: DV360LineItemType;
};

type PositionOptions = {
  devicePlatforms: string[];
  publisherPlatforms: string[];
  audienceNetworkPositions: AudienceNetworkPosition[];
  facebookPositions: FacebookPosition[];
  instagramPositions: InstagramPosition[];
  messengerPositions: MessengerPosition[];
};

interface AdGroup {
  adGroupId: string;
  campaignId: number;
  id: number;
  name: string;
  status: ObjectStatus;
  numOfAds: number;
  customParams?: Record<string, string>;
  adGroupParams?: Partial<Record<SocialAdGroupParamKey, AdGroupParam>>;
  campaignVersionId: number;
  campaignIntegrationId: number;
  location: {
    name: string;
    lat: number;
    lon: number;
  };

  // Only in Facebook
  positionOptions?: PositionOptions;

  startDate: DateTime;
  endDate: DateTime;

  //Only for DV360
  lineItem?: DV360LineItem;

  supportedAdTypes?: YoutubeAdType[];
  productSetData: ProductSetData;
}

interface RemoteAdGroup
  extends Omit<AdGroup, 'adGroupParams' | 'positionOptions' | 'startDate' | 'endDate'> {
  adGroupParams: AdGroupParam[];
  positionOptions: {
    device_platforms: string[];
    publisher_platforms: string[];
    audience_network_positions: AudienceNetworkPosition[];
    facebook_positions: FacebookPosition[];
    instagram_positions: InstagramPosition[];
    messenger_positions: MessengerPosition[];
  };

  startDate?: string;
  endDate?: string;
}

//TODO_MAKHO: after creative card refactor, add default values in the dataConverter and make them mandatory
type BaseSocialAdData = {
  externalAdPrimaryText?: string[];
  externalAdHeadline?: string[];
  externalAdDescription?: string[];
  externalAdCTA?: string;
  externalAdLongHeadline?: string[];
  clickTrackers?: string[];
  impressionTrackers?: string[];
  trackingPixels?: SocialTrackingPixel[];
};

type GenerateAdsChanges = {
  creativesIds: number[];
  feedsIds: number[];
  adGroupsIds: number[];
  cardsIds: number[];
};

type FacebookSocialAdData = BaseSocialAdData;
type TiktokSocialAdData = BaseSocialAdData;
type YoutubeSocialAdData = BaseSocialAdData & {
  externalAdLongHeadline?: string[];
};
type SnapchatSocialAdData = BaseSocialAdData;
type LinkedInSocialAdData = BaseSocialAdData;
type SocialAdData =
  | FacebookSocialAdData
  | TiktokSocialAdData
  | YoutubeSocialAdData
  | SnapchatSocialAdData
  | LinkedInSocialAdData;

type BaseSocialAdDetails = BaseSocialAdData & {
  posterUrl: string;
  medias: SocialAdMedia[];
  identityId?: string;
  displayLandingPage?: string;
  landingPage: string;
  identity: YoutubeChannelIdentity;
};

type FacebookSocialAdDetails = BaseSocialAdDetails &
  FacebookSocialAdData & {
    trackingPixelId?: string;
    additionalIdentityId?: string;
    carouselMediaId?: number;
  };

type YoutubeSocialAdDetails = BaseSocialAdDetails & YoutubeSocialAdData;

type TiktokSocialAdDetails = BaseSocialAdDetails &
  TiktokSocialAdData & {
    trackingPixelId?: string;
  };

type SnapchatSocialAdDetails = BaseSocialAdDetails & SnapchatSocialAdData;

type LinkedInSocialAdDetails = BaseSocialAdDetails & LinkedInSocialAdData;

enum AdType {
  Video = 'video',
  Image = 'image',
  Carousel = 'carousel',
}

type SocialAdDetails = FacebookSocialAdDetails | TiktokSocialAdDetails | YoutubeSocialAdDetails;

interface ProductSetData {
  catalogId: string;
  catalogName: string;
  productSetId: string;
  productSetName: string;
}

type SocialAdMedia = {
  id: number;
  format: string;
  extId: string;
};

type BaseRemoteSocialAd = BaseSocialAdDetails & {
  id: number;
  campaignId: number;
  creativeId: number;
  status: ExternalAdStatus;
  externalAdId: string;
  externalAdName: string;
  adGroupId: number;
  adGroup: RemoteAdGroup;
  issues: CLIssues;
  lastPublished: string;
  isActive: boolean;
  isCTAFromAd: boolean;
  identityId: string;
  identityName: string;
  isLongHeadlineFromAd: boolean;
  isDescriptionFromAd: boolean;
  isHeadlineFromAd: boolean;
  isPrimaryTextFromAd: boolean;
  externalCampaignID: string;
  externalCampaignName: string;
  publishedOnVersionId: number;
  displayLandingPage?: string;
  creativeNodeId?: number;
  pausedAdDetails?: RemotePausedAdDetails;
  productFeedId?: number;
  products?: string[];
  adType: SocialAdType;
  externalMediaId: string;
  externalMediaName: string;
  contentFeedItems?: {
    id: number;
    feedId: number;
  }[];
};

type FacebookRemoteSocialAd = BaseRemoteSocialAd &
  FacebookSocialAdDetails & {
    productSetData: ProductSetData;
    isCarousel: boolean;
    additionalIdentityId?: string;
    additionalIdentityName?: string;
    additionalIdentityIconUrl?: string;
    trackingPixelId?: string;
    //only for carousel cards
    cardsInfo?: {
      creativeId: number;
      posterUrl: string;
      externalAdDescription: string;
      externalAdHeadline: string;
      externalAdLongHeadline: string;
      isDescriptionFromAd: boolean;
      isDisplayLadingPageFromAd: boolean;
      isHeadlineFromAd: boolean;
      displayLandingPage: string;
      feedId: number;
      mainItemIds: string[];
      landingPage?: string;
      previewItemsUrls: string[];
      mediaId: number;
      isImageType: boolean;
      isVideoType: boolean;
    }[];
    publishedAdDetails?: FacebookSocialAdDetails & {
      mediaIds: number[];
    };
  };

type YoutubeRemoteSocialAd = BaseRemoteSocialAd &
  YoutubeSocialAdDetails & {
    isLongHeadlineFromAd: boolean;
    publishedAdDetails?: YoutubeSocialAdDetails & {
      mediaIds: number[];
    };
  };

type TiktokRemoteSocialAd = BaseRemoteSocialAd &
  TiktokSocialAdDetails & {
    trackingPixelId?: string;
    publishedAdDetails?: TiktokSocialAdDetails & {
      mediaIds: number[];
    };
  };

type SnapchatRemoteSocialAd = BaseRemoteSocialAd &
  SnapchatSocialAdDetails & {
    publishedAdDetails?: SnapchatSocialAdDetails & {
      mediaIds: number[];
    };
  };

type LinkedInRemoteSocialAd = BaseRemoteSocialAd &
  LinkedInSocialAdDetails & {
    publishedAdDetails?: LinkedInSocialAdDetails & {
      mediaIds: number[];
    };
    isCarousel: boolean;
    cardsInfo?: {
      creativeId: number;
      posterUrl: string;
      externalAdDescription: string;
      externalAdHeadline: string;
      isDescriptionFromAd: boolean;
      isDisplayLadingPageFromAd: boolean;
      isHeadlineFromAd: boolean;
      displayLandingPage: string;
      feedId: number;
      mainItemIds: string[];
      landingPage?: string;
      previewItemsUrls: string[];
    }[];
  };

type RemoteSocialAd =
  | FacebookRemoteSocialAd
  | TiktokRemoteSocialAd
  | YoutubeRemoteSocialAd
  | SnapchatRemoteSocialAd
  | LinkedInRemoteSocialAd;

interface RemotePausedAdDetails {
  pauseDate: string;
  pausedBy: IntersectionUser;
  isPausedBySystem: boolean;
}

interface PausedAdDetails extends Omit<RemotePausedAdDetails, 'pauseDate'> {
  pauseDate: DateTime;
}

type FacebookSocialAd = Omit<
  FacebookRemoteSocialAd,
  'lastPublished' | 'pausedAdDetails' | 'adGroup'
> & {
  lastPublished: DateTime;
  pausedAdDetails?: PausedAdDetails;
  adGroup: AdGroup;
};

type YoutubeSocialAd = Omit<
  YoutubeRemoteSocialAd,
  'lastPublished' | 'pausedAdDetails' | 'adGroup'
> & {
  lastPublished: DateTime;
  pausedAdDetails?: PausedAdDetails;
  adGroup: AdGroup;
  adType: YoutubeAdType;
};

type TiktokSocialAd = Omit<
  TiktokRemoteSocialAd,
  'lastPublished' | 'pausedAdDetails' | 'adGroup'
> & {
  lastPublished: DateTime;
  pausedAdDetails?: PausedAdDetails;
  adGroup: AdGroup;
};

type SnapchatSocialAd = Omit<
  SnapchatRemoteSocialAd,
  'lastPublished' | 'pausedAdDetails' | 'adGroup'
> & {
  lastPublished: DateTime;
  pausedAdDetails?: PausedAdDetails;
  adGroup: AdGroup;
};

type LinkedInSocialAd = Omit<
  LinkedInRemoteSocialAd,
  'lastPublished' | 'pausedAdDetails' | 'adGroup'
> & {
  lastPublished: DateTime;
  pausedAdDetails?: PausedAdDetails;
  adGroup: AdGroup;
  adType: LinkedInAdType;
};

type SocialAd =
  | FacebookSocialAd
  | TiktokSocialAd
  | YoutubeSocialAd
  | SnapchatSocialAd
  | LinkedInSocialAd;

type RemoteAdsRenderingStatus = {status: string; count: number}[];

interface AdsRenderingStatus {
  published: number;
  failed: number;
  pending: number;
  planned: number;
  plannedForUpdate: number;
  plannedForDeletion: number;
  syncingWithPlatform: number;
  pendingSync: number;
  failedGenerate: number;
  removedFromPlatform: number;
}

interface CarouselItem {
  desc: string;
  headline: string;
  landingPage: string;
  mediaUrl: string;
  mediaType: ShortMediaType;
}

interface YoutubeChannelIdentity {
  iconUrl: string;
  id: string;
  macro: string;
  name: string;
}

type SocialAdParams = {
  primaryText?: string[];
  cta?: string;
  headline?: string[];
  desc?: string[];
  adType?: SocialAdType;
};

type TikTokAdParams = SocialAdParams & {
  identity?: SocialNetworkPage;
};

type SnapchatAdParams = SocialAdParams & {
  identity?: SocialNetworkPage;
};

type FacebookAdParams = SocialAdParams & {
  ogDescription?: string;
  ogTitle?: string;
  isCarousel?: boolean;
  carouselItems?: CarouselItem[];
  identity?: SocialNetworkPage;
  additionalIdentity?: SocialNetworkPage;
};

type YoutubeAdParams = SocialAdParams & {
  adType?: YoutubeAdType;
  displayLandingPage?: string;
  longHeadline?: string[];
  identity: YoutubeChannelIdentity;
};

type LinkedInAdParams = SocialAdParams & {
  identity?: SocialNetworkPage;
};

enum SocialAdPropertyType {
  PrimaryText = 'externalAdPrimaryText',
  Description = 'externalAdDescription',
  Headline = 'externalAdHeadline',
  LongHeadline = 'externalAdLongHeadline',
  CTA = 'externalAdCTA',
  ClickTrackers = 'clickTrackers',
  ImpressionTrackers = 'impressionTrackers',
}

enum TiktokAdPropertyType {
  PrimaryText = 'externalAdPrimaryText',
  Description = 'externalAdDescription',
  Headline = 'externalAdHeadline',
  CTA = 'externalAdCTA',
}

enum FacebookPlacementType {
  Facebook = 'Facebook',
  Instagram = 'Instagram',
  Messenger = 'Messenger',
  AudienceNetwork = 'Audience Network',
}

enum YoutubeAdType {
  Skippable = 10,
  InFeed = 20,
  NonSkippable = 30,
  Bumper = 40,
  Responsive = 50,
  Shorts = 60,
}

enum FacebookAdType {
  FacebookImage = 110,
  FacebookVideo = 120,
  FacebookCarousel = 130,
}

enum SnapchatAdType {
  //this split up is just for the Backend to know the ad type
  //in the FE we have Video or Image creative card
  SnapchatImage = 210,
  SnapchatVideo = 220,
}

enum LinkedInAdType {
  LinkedInImage = 310,
  LinkedInVideo = 320,
  LinkedInCarousel = 330,
}

enum TiktokAdType {
  TiktokVideo = 410,
}

type SocialAdType = YoutubeAdType | FacebookAdType | SnapchatAdType | LinkedInAdType | TiktokAdType;

interface RemoteAdForClient {
  id: number;
  posterUrl: string;
}

interface AdForClient extends Omit<RemoteAdForClient, 'id' | 'posterUrl'> {
  id: number;
  poster: string;
}

interface AdStatusInfo {
  adReviewStatus: AdReviewStatus;
  adState: AdState;
  isActive: boolean;
  reasons: string[];
}

type RemoteAdStatus = Record<string, AdStatusInfo>;

type GenerateAdsValidationIssue = {
  issue: GenerateAdsIssue;
  reasons: string[];
};

enum ExceededLimitError {
  AdGroupsExcceededLimit = 0,
  AdGroupLimitNumber = 1,
}

type RemoteGenerateAdsValidation = {
  status: GenerateAdsStatus;
  changes: GenerateAdsChanges;
  issues: GenerateAdsValidationIssue[];
};

enum PauseAdsAction {
  Pause = 1,
  KeepUnchanged = 2,
  KeepUnchangedAndDeleted = 3,
}

enum DeletedAdsAction {
  Delete = 1,
  TurnOff = 2,
}

type RemoteCampaignUpdateInfo = {
  lastUpdate: string;
};

type LocalCampaignUpdateInfo = RemoteCampaignUpdateInfo;

type RemoteAdMedia = {
  adMedias: ClinchMedia[];
  publishedAdMedias: ClinchMedia[];
};

type SocialCampaignFlowStatusMapperValue = {
  uiName: string;
  icon: string;
};

enum SocialAdTypeName {
  Video = 'Video',
  Image = 'Image',
  Carousel = 'Carousel',
}

enum SocialAdMediaRenderingStatus {
  SentToRender = 5,
  Rendered = 10,
  Failed = 20,
}

type RemoteMediaRenderingStatusByMediaId = Record<number, SocialAdMediaRenderingStatus>;

type LocalMediaRenderingStatusByMediaId = RemoteMediaRenderingStatusByMediaId;

type GetProductSetsForCatalogParams = {
  campaignId: number;
  versionId: number;
  catalogId: string;
};

type RemoteProductSet = {
  id: string;
  name: string;
};

type ClientProductSet = RemoteProductSet;

interface DisplayedExternalAdParams {
  id: number;
  externalAdId: string;
}
type BaseSocialAdForTable = Partial<AdStatusInfo> & {
  id: string;
  adId: number;
  cardIndex: number;
  group?: boolean;
  externalAdPrimaryText?: string | string[];
  externalAdHeadline?: string | string[];
  externalAdDescription?: string | string[];
  campaignIntegrationId?: number;
  mediaRenderingStatus: SocialAdMediaRenderingStatus;
  showMediaColumnInfo: boolean;
};

type TiktokAdForTable = TiktokSocialAd &
  BaseSocialAdForTable & {
    externalAdLongHeadline?: string | string[];
    feedDetails?: {
      feedId?: number;
      mainItemIds?: string[];
    };
  };

type FacebookAdForTable = FacebookSocialAd &
  Partial<
    Omit<
      NonNullable<FacebookSocialAd['cardsInfo']>[number],
      'externalAdPrimaryText' | 'externalAdHeadline' | 'externalAdDescription'
    >
  > &
  BaseSocialAdForTable & {
    isCarousel: boolean;
    carouselId: string;
    carouselMediaId: number;
    externalAdLongHeadline?: string | string[];
    feedDetails?: {
      feedId?: number;
      mainItemIds?: string[];
    };
    pixels: SocialTrackingPixel[];
  };

type YoutubeAdForTable = YoutubeSocialAd &
  BaseSocialAdForTable & {
    externalAdLongHeadline?: string | string[];
    feedDetails?: {
      feedId?: number;
      mainItemIds?: string[];
    };
  } & DoubleClickCampaignManagerParams & {
    //only for DV360;
    lineItem?: DV360LineItem;
  };

type SnapchatAdForTable = SnapchatSocialAd &
  BaseSocialAdForTable & {
    externalAdLongHeadline?: string | string[];
    feedDetails: {
      feedId: number;
      mainItemIds: string[];
    };
    brandName: string;
  };

type LinkedInAdForTable = LinkedInSocialAd &
  BaseSocialAdForTable & {
    feedDetails: {
      feedId: number;
      mainItemIds: string[];
    };
  };

type SocialAdForTable =
  | FacebookAdForTable
  | YoutubeAdForTable
  | TiktokAdForTable
  | SnapchatAdForTable
  | LinkedInAdForTable;

type SocialAdsTableProps = {
  mediaStatus?: ExternalAdStatus;
  adGroupId?: number;
  nodeId?: number;
  isForPreview?: boolean;
  onSelectionChange?: Dispatch<SocialAdForTable[]>;
};

type FacebookAdsTableProps = SocialAdsTableProps & {
  onSelectionChange?: Dispatch<FacebookAdForTable[]>;
};
type YoutubeAdsTableProps = SocialAdsTableProps & {
  onSelectionChange?: Dispatch<YoutubeAdForTable[]>;
};
type TiktokAdsTableProps = SocialAdsTableProps & {
  onSelectionChange?: Dispatch<TiktokAdForTable[]>;
};
type SnapchatAdsTableProps = SocialAdsTableProps & {
  onSelectionChange?: Dispatch<SnapchatAdForTable[]>;
};
type LinkedInAdsTableProps = SocialAdsTableProps & {
  onSelectionChange?: Dispatch<LinkedInAdForTable[]>;
};

enum AdsTableColumns {
  CarouselId = 'carouselId',
  Type = 'type',
  AdType = 'adType',
  PosterUrl = 'posterUrl',
  Status = 'status',
  IsActive = 'isActive',
  ExternalAdId = 'externalAdId',
  ExternalAdName = 'externalAdName',
  AdSetId = 'adSetId',
  AdSetName = 'adSetName',
  AdSet = 'adSet',
  CreativeId = 'creativeId',
  Medias = 'medias',
  FeedId = 'feedId',
  Feed = 'feed',
  DisplayLandingPage = 'displayLandingPage',
  Description2 = 'description2',
  IdentityId = 'identityId',
  IdentityName = 'identityName',
  AdditionalIdentityId = 'additionalIdentityId',
  AdditionalIdentityName = 'additionalIdentityName',
  TrackingPixelId = 'trackingPixelId',
  WebsiteEvent = 'websiteEvent',
  OfflineEvent = 'offlineEvent',
  LandingPage = 'landingPage',
  LastPublished = 'lastPublished',
  Id = 'id',
  AdId = 'adId',
  Catalog = 'catalog',
  ProductSet = 'productSet',
  CampaignId = 'CampaignId',
  CampaignName = 'campaignName',
  Video = 'video',
  BrandName = 'brandName',
  DcmTrackingPlacementId = 'dcmTrackingPlacementId',
  DcmTrackingAdId = 'dcmTrackingAdId',
  DcmTrackingCreativeId = 'dcmTrackingCreativeId',
  ContentFeedItem = 'contentFeedItem',
  ContentFeedId = 'contentFeedId',
  LineItemId = 'lineItemId',
  LineItemName = 'lineItemName',
  ProductFeedId = 'productFeedId',
  ProductId = 'productId',
  CM360Fields = 'cm360Fields',
}

enum AdStatus {
  All = 'All',
  Active = 'Active',
  Inactive = 'Inactive',
}

export {
  SocialAd,
  RemoteAdGroup,
  ExternalAdStatus,
  AdsRenderingStatus,
  RemoteAdsRenderingStatus,
  FacebookPosition,
  SocialAdParams,
  AdGroup,
  InstagramPosition,
  AudienceNetworkPosition,
  MessengerPosition,
  SocialAdGroupParamKey,
  AdGroupParam,
  SocialAdData,
  SocialAdDetails,
  SocialAdPropertyType,
  TiktokAdPropertyType,
  AdType,
  FacebookPlacementType,
  YoutubeAdType,
  FacebookAdType,
  SnapchatAdType,
  TiktokAdType,
  SocialAdType,
  RemoteAdForClient,
  AdForClient,
  RemoteAdStatus,
  RemoteSocialAd,
  PausedAdDetails,
  AdReviewStatus,
  AdStatusInfo,
  CarouselItem,
  YoutubeAdParams,
  FacebookAdParams,
  TikTokAdParams,
  SnapchatAdParams,
  FacebookSocialAd,
  YoutubeSocialAd,
  TiktokSocialAd,
  SnapchatSocialAd,
  FacebookSocialAdData,
  TiktokSocialAdData,
  YoutubeSocialAdData,
  SnapchatSocialAdData,
  FacebookSocialAdDetails,
  TiktokSocialAdDetails,
  YoutubeSocialAdDetails,
  GenerateAdsStatus,
  GenerateAdsIssue,
  GenerateAdsChanges,
  RemoteGenerateAdsValidation,
  GenerateAdsValidationIssue,
  SocialCampaignFlowStatus,
  PauseAdsAction,
  DeletedAdsAction,
  ExceededLimitError,
  LinkedInAdType,
  LinkedInSocialAdData,
  LinkedInSocialAd,
  LinkedInSocialAdDetails,
  CarouselProductSelectionType,
  LinkedInAdParams,
  RemoteCampaignUpdateInfo,
  LocalCampaignUpdateInfo,
  RemoteAdMedia,
  SocialAdTypeName,
  LocalMediaRenderingStatusByMediaId,
  RemoteMediaRenderingStatusByMediaId,
  SocialAdMediaRenderingStatus,
  DV360LineItemType,
  DV360LineItem,
  SocialCampaignFlowStatusMapperValue,
  PositionOptions,
  GetProductSetsForCatalogParams,
  RemoteProductSet,
  ClientProductSet,
  AdState,
  DisplayedExternalAdParams,
  TiktokAdForTable,
  FacebookAdForTable,
  YoutubeAdForTable,
  SnapchatAdForTable,
  SocialAdForTable,
  SocialAdsTableProps,
  FacebookAdsTableProps,
  YoutubeAdsTableProps,
  TiktokAdsTableProps,
  SnapchatAdsTableProps,
  AdsTableColumns,
  AdStatus,
  LinkedInAdsTableProps,
  LinkedInAdForTable,
  SocialAdMedia,
};
